import { parseHexString } from "@mm/color-lib";

export const isValidColorValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }
  if (parseHexString(trimmed)) {
    return true;
  }

  const rgbaMatch = trimmed.replace(/\s+/g, "").match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbaMatch) {
    return false;
  }

  const parts = rgbaMatch[1].split(",");
  if (parts.length !== 3 && parts.length !== 4) {
    return false;
  }

  const toUnit = (raw: string, channelIndex: number): number => {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      return Number.NaN;
    }
    if (channelIndex < 3) {
      if (n <= 1) {
        return Math.min(Math.max(n, 0), 1);
      }
      return Math.min(Math.max(n / 255, 0), 1);
    }
    return Math.min(Math.max(n, 0), 1);
  };

  const r = toUnit(parts[0], 0);
  const g = toUnit(parts[1], 1);
  const b = toUnit(parts[2], 2);
  const a = parts[3] == null ? 1 : toUnit(parts[3], 3);

  return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) && Number.isFinite(a);
};

// Text colors used on colored backgrounds: neutral-800 (dark text) and white.
const READABLE_TEXT_DARK = "#1b1e25";
const READABLE_TEXT_LIGHT = "#ffffff";

/**
 * Parses a hex or rgb()/rgba() color into RGB channels normalized to [0, 1].
 * Returns null for any other format (e.g. named colors).
 */
const parseColorToRgb = (value: string): [number, number, number] | null => {
  const trimmed = value.trim();
  // parseHexString accepts some non-hex words (e.g. "blue"), so check the characters first
  if (/^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(trimmed)) {
    const hex = parseHexString(trimmed);
    if (hex) {
      return [hex[0], hex[1], hex[2]];
    }
  }

  const rgbMatch = trimmed.replace(/\s+/g, "").match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbMatch) {
    return null;
  }

  const parts = rgbMatch[1].split(",");
  if (parts.length !== 3 && parts.length !== 4) {
    return null;
  }

  const channels = parts.slice(0, 3).map((raw) => Number(raw));
  if (channels.some((n) => !Number.isFinite(n))) {
    return null;
  }
  // Same convention as isValidColorValue: values <= 1 are already normalized, others are 0-255
  return channels.map((n) => (n <= 1 ? Math.max(n, 0) : Math.min(n / 255, 1))) as [number, number, number];
};

const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const toLinear = (channel: number) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const darkTextLuminance = relativeLuminance(parseColorToRgb(READABLE_TEXT_DARK) ?? [0, 0, 0]);

/**
 * Returns the text color with the best WCAG contrast on the given background color,
 * or undefined if the background cannot be parsed (the caller keeps the default text color).
 */
export const getReadableTextColor = (backgroundColor: string): string | undefined => {
  const rgb = parseColorToRgb(backgroundColor);
  if (!rgb) {
    return undefined;
  }

  const luminance = relativeLuminance(rgb);
  const contrastWithDark = (luminance + 0.05) / (darkTextLuminance + 0.05);
  const contrastWithLight = 1.05 / (luminance + 0.05);
  return contrastWithDark >= contrastWithLight ? READABLE_TEXT_DARK : READABLE_TEXT_LIGHT;
};
